import React from 'react';
import { ExternalLinkIcon, VideoIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { images } from '../data/images';
import { site } from '../data/site';
import { testimonials } from '../data/testimonials';
import { buttonStyles, container } from '../utils/styles';
import { PageHeader } from '../components/shared/PageHeader';
import { SectionHeading } from '../components/shared/SectionHeading';
import { StarRating } from '../components/shared/StarRating';
import { TestimonialsDark } from '../components/shared/TestimonialsDark';
import { CtaBand } from '../components/shared/CtaBand';
export function Testimonials() {
  const {
    t
  } = useLanguage();
  useDocumentMeta('testimonials');
  return <>
      <PageHeader crumb={t({
      en: 'Testimonials',
      es: 'Testimonios'
    })} title={t({
      en: 'Families Trust Us With the People They Love',
      es: 'Las Familias Nos Confían a Quienes Más Aman'
    })} intro={t({
      en: 'Every review below is from a real family on Google. We’re honored by each one.',
      es: 'Cada reseña es de una familia real en Google. Nos honra cada una de ellas.'
    })} data-id="element-596" data-mp="1nujddt45dubw">
        <div className="rounded-2xl bg-white p-7 text-ink" data-id="element-597" data-mp="238qj4s8ljx96">
          <div className="flex items-center gap-5" data-id="element-598" data-mp="287taxazkg6z7">
            <span className="font-display text-6xl font-bold text-brand-dark" data-id="element-599" data-mp="286ic7pkz9pey">5.0</span>
            <div data-id="element-600" data-mp="1bilqk6aoo3wv">
              <StarRating size="lg" label={t({
              en: '5 out of 5 stars',
              es: '5 de 5 estrellas'
            })} data-id="element-601" data-mp="1bj3xbwuz20j1" />
              <p className="mt-1 text-ink-soft" data-id="element-602" data-mp="1fkp5x60h3snf">
                {t({
                en: 'Based on 3 Google reviews',
                es: 'Basado en 3 reseñas de Google'
              })}
              </p>
            </div>
          </div>
          <a href={site.googleReviewsUrl} target="_blank" rel="noopener noreferrer" className={`${buttonStyles.outline} mt-6 w-full`} data-id="element-603" data-mp="1pepexiyvli1f">
            {t({
            en: 'See us on Google',
            es: 'Vernos en Google'
          })}
            <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-604" data-mp="30y1wzdx83bj2" />
          </a>
        </div>
      </PageHeader>

      <TestimonialsDark showAllLink={false} data-id="element-605" data-mp="268qmsd1v5xt4" />

      <section className="bg-white py-20 sm:py-24" aria-labelledby="impact-heading" data-id="element-606" data-mp="267xcx37lys6r">
        <div className={`${container} grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-16`} data-id="element-607" data-mp="2l69815fc1svt">
          <div className="lg:col-span-5" data-id="element-608" data-mp="2g6myb5ypqurr">
            <img src={images.family.src} alt={t(images.family.alt)} loading="lazy" className="h-80 w-full rounded-2xl object-cover sm:h-[30rem]" data-id="element-609" data-mp="2g4toxiwptwg2" />
          </div>
          <div className="lg:col-span-7" data-id="element-610" data-mp="1njabcuon4kxx">
            <SectionHeading id="impact-heading" title={t({
            en: 'Impact Stories',
            es: 'Historias de Impacto'
          })} intro={t({
            en: 'Behind every review is a family that found relief, and a loved one who felt cared for.',
            es: 'Detrás de cada reseña hay una familia que encontró alivio y un ser querido que se sintió cuidado.'
          })} data-id="element-611" data-mp="1ng4n1hnu5n39" />
            <div className="mt-8 divide-y divide-mist border-t border-mist" data-id="element-612" data-mp="3gfcvfjjw9m6p">
              {testimonials.map(item => <article key={item.id} className="py-6" data-id="element-613" data-mp="3l6ux69tqrqsq">
                  <h3 className="text-xl font-bold text-ink" data-id="element-614" data-mp="36gx3xfgygawo">{t(item.storyTitle)}</h3>
                  <p className="mt-2 text-lg leading-relaxed text-ink-soft" data-id="element-615" data-mp="36eu6iadh3fhx">{t(item.story)}</p>
                  <p className="mt-2 text-sm font-medium text-gold-dark" data-id="element-616" data-mp="3bdxarslmphou">— {item.name}</p>
                </article>)}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16" aria-labelledby="share-heading" data-id="element-617" data-mp="7yi14sh5xa48">
        <div className={`${container} flex flex-col gap-6 rounded-2xl bg-white p-8 sm:p-10 md:flex-row md:items-center md:justify-between`} data-id="element-618" data-mp="3q44hxc66rea8">
          <div className="flex gap-5" data-id="element-619" data-mp="3u8cbbmnl9ies">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gold-light text-gold-dark" aria-hidden="true" data-id="element-620" data-mp="2ephhblfeeo6g">
              <VideoIcon className="h-7 w-7" data-id="element-621" data-mp="2ip84lf5pum68" />
            </span>
            <div data-id="element-622" data-mp="2yzimstirkms2">
              <h2 id="share-heading" className="text-2xl font-bold text-ink" data-id="element-623" data-mp="2z01znvtk4kz8">
                {t({
                en: 'Video stories are coming soon',
                es: 'Pronto tendremos historias en video'
              })}
              </h2>
              <p className="mt-2 max-w-xl text-ink-soft" data-id="element-624" data-mp="2p8xiqqkbmq1r">
                {t({
                en: 'Has A.M. Home Care cared for your family? We’d be honored if you shared your experience on Google.',
                es: '¿A.M. Home Care ha cuidado a tu familia? Nos honraría que compartieras tu experiencia en Google.'
              })}
              </p>
            </div>
          </div>
          <a href={site.googleReviewsUrl} target="_blank" rel="noopener noreferrer" className={buttonStyles.primary} data-id="element-625" data-mp="37xkm94mun7d8">
            {t({
            en: 'Leave a Review',
            es: 'Dejar una Reseña'
          })}
          </a>
        </div>
      </section>

      <CtaBand data-id="element-626" data-mp="1whnmsmhdx51r" />
    </>;
}